import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import to from 'await-to-js'
import axios from 'axios'
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Globe,
  Mail,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  User,
} from 'lucide-react'

import FbButton from 'components/FbUI/FbButton'
import { cn } from 'components/UI/cn'
import ReactSelectWrapper from 'components/FormUtils/ReactSelectWrapper'
import TimePicker from 'components/FormUtils/TimePicker'
import { handleFormError } from 'utils/handleFormError'

import { WORKFLOW_STATUS } from 'models/campaign'
import { LiveCampaignBanner } from './LiveCampaignBanner'
import { WizardStepFooter, WIZARD_BUTTON_MIN_W } from './WizardStepFooter'
import { WizardStepHeader } from './WizardStepHeader'
import { useGetCampaignLaunchReadiness } from '../campaign_insights_api'
import { useSyncCampaignLeads } from '../leads_api'
import {
  useGetCampaignSenderEmails,
  useSyncWorkspaceSenderEmails,
  useDetachCampaignSenderEmail,
  type SenderEmail,
} from '../sender_emails_api'
import { useGetCampaignSequence } from '../sequences_api'
import { AddSenderEmailsModal } from '../SequenceTab/AddSenderEmailsModal'
import { TIMEZONE_OPTIONS } from '../SequenceTab/timezones'
import {
  useGetCampaignSchedule,
  useUpdateCampaignSchedule,
} from '../campaign_schedule_api'
import {
  useGetCampaignSettings,
  useUpdateCampaignSettings,
} from '../campaign_settings_api'

interface WizardSettingsStepProps {
  campaignId: number
  onNext: () => void
  onBack: () => void
  hasContacts: boolean
  stepNumber: number
  totalSteps: number
  onSaveExit?: () => void
  isLiveCampaign?: boolean
  readOnly?: boolean
  campaignStatus?: string
}

interface WizardSectionHandle {
  submitIfDirty: () => Promise<boolean>
}

interface CampaignScheduleWizardForm {
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  sunday: boolean
  start_time: string
  end_time: string
  timezone: string
}

const SCHEDULE_DEFAULT_VALUES: CampaignScheduleWizardForm = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
  start_time: '08:00',
  end_time: '17:00',
  timezone: 'America/New_York',
}

const DAYS: Array<{
  key: keyof Pick<
    CampaignScheduleWizardForm,
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday'
  >
  label: string
}> = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
]

const SCHEDULE_PRESETS: Array<{
  label: string
  description: string
  values: CampaignScheduleWizardForm
}> = [
  {
    label: 'Business Hours',
    description: 'Mon-Fri, 8am-5pm',
    values: {
      ...SCHEDULE_DEFAULT_VALUES,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
      start_time: '08:00',
      end_time: '17:00',
    },
  },
  {
    label: 'Morning Focus',
    description: 'Mon-Fri, 7am-11am',
    values: {
      ...SCHEDULE_DEFAULT_VALUES,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
      start_time: '07:00',
      end_time: '11:00',
    },
  },
  {
    label: 'Extended Hours',
    description: 'Mon-Sat, 7am-7pm',
    values: {
      ...SCHEDULE_DEFAULT_VALUES,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: false,
      start_time: '07:00',
      end_time: '19:00',
    },
  },
  {
    label: 'Afternoons Only',
    description: 'Mon-Fri, 12pm-5pm',
    values: {
      ...SCHEDULE_DEFAULT_VALUES,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
      start_time: '12:00',
      end_time: '17:00',
    },
  },
]

function toTimePickerFormat(time: string | undefined): string {
  if (!time) return '08:00'
  if (/^\d{2}:\d{2}$/.test(time)) return time
  const match = time.match(/^(\d{2}:\d{2})/)
  return match ? match[1] : '08:00'
}

function getSenderStatusLabel(status: string): string {
  switch (status) {
    case 'connected':
      return 'Connected'
    case 'not_connected':
      return 'Not Connected'
    case 'pending_move':
      return 'Pending Move'
    case 'pending_deletion':
      return 'Pending Deletion'
    default:
      return status
  }
}

function getSenderStatusClassName(status: string): string {
  switch (status) {
    case 'connected':
      return 'bg-success-bg text-success'
    case 'pending_move':
      return 'bg-warning-bg text-warning'
    case 'pending_deletion':
      return 'bg-error-bg text-error'
    default:
      return 'bg-surface-alt text-fg-secondary'
  }
}

function getSenderInitials(sender: SenderEmail): string {
  if (sender.name?.trim()) {
    const parts = sender.name.trim().split(/\s+/)
    return parts
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('')
  }
  return sender.email.slice(0, 2).toUpperCase()
}

export function WizardSettingsStep({
  campaignId,
  onNext,
  onBack,
  hasContacts,
  stepNumber,
  totalSteps,
  onSaveExit,
  isLiveCampaign = false,
  readOnly = false,
  campaignStatus,
}: WizardSettingsStepProps) {
  const scheduleRef = useRef<WizardSectionHandle | null>(null)
  const { data: sequence } = useGetCampaignSequence(campaignId)
  const bisonCampaignId = sequence?.bison_campaign_id ?? 0
  const { refetch: refetchReadiness } = useGetCampaignLaunchReadiness(
    campaignId,
    hasContacts
  )
  const syncCampaignLeadsMutation = useSyncCampaignLeads(
    bisonCampaignId,
    campaignId
  )

  const saveDirtySections = async () => {
    return scheduleRef.current?.submitIfDirty() ?? true
  }

  const handleContinue = async () => {
    const saveSucceeded = await saveDirtySections()
    if (!saveSucceeded) return

    if (hasContacts && bisonCampaignId > 0) {
      const [syncErr] = await to(syncCampaignLeadsMutation.mutateAsync())
      if (syncErr) {
        toast.error('Sync contacts before continuing to launch')
        return
      }
    }

    const readinessResult = await refetchReadiness()
    const readiness = readinessResult.data

    if (!readiness?.sender_emails.is_complete) {
      const disconnected = readiness?.sender_emails.disconnected_emails ?? []
      toast.error(
        disconnected.length > 0
          ? `Reconnect mailbox(es) before continuing: ${disconnected.join(', ')}`
          : 'Attach at least one sender email before continuing'
      )
      return
    }

    if (!readiness?.schedule.is_complete) {
      toast.error('Create and save a sending schedule before continuing')
      return
    }

    onNext()
  }

  const handleSaveExit = async () => {
    const saveSucceeded = await saveDirtySections()
    if (!saveSucceeded) return
    onSaveExit?.()
  }

  return (
    <div className="flex h-full flex-col">
      <WizardStepHeader
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        title="Campaign Settings"
        description="Choose who sends this campaign and when emails are allowed to go out."
      />

      <div className="flex-1 overflow-auto px-8 py-6">
        <div className="mx-auto max-w-[1100px] space-y-6">
          {readOnly && (
            <div className="flex items-start gap-2 rounded-xl border border-warning-border bg-warning-bg px-4 py-3 text-sm text-warning">
              This campaign is active. Pause the campaign to edit settings.
            </div>
          )}
          {isLiveCampaign && !readOnly && <LiveCampaignBanner />}
          <fieldset
            disabled={readOnly}
            className={cn(readOnly && 'opacity-60', 'space-y-6')}
          >
            <SettingsSection
              icon={User}
              title="Sender & Domain"
              description="Each sender is tied to a sending domain. Pick which connected accounts can send this campaign."
            >
              <WizardSenderDomainSection
                campaignId={campaignId}
                campaignStatus={campaignStatus}
              />
            </SettingsSection>

            <SettingsSection
              icon={Calendar}
              title="Send Schedule"
              description="Choose which days and hours emails are sent, with presets to get started quickly."
            >
              <WizardScheduleSection
                ref={scheduleRef}
                campaignId={campaignId}
              />
            </SettingsSection>

            <SettingsSection
              icon={Mail}
              title="Email Format"
              description="Plain text is recommended for cold outreach. HTML may hurt deliverability with major filters."
            >
              <WizardEmailFormatSection campaignId={campaignId} />
            </SettingsSection>
          </fieldset>

          <div className="rounded-xl bg-surface-alt px-4 py-3 text-xs text-fg-secondary">
            <p className="flex items-start gap-2 leading-relaxed">
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-fg-secondary" />
              <span>
                Email signature and reply opt-out detection are managed in{' '}
                <Link
                  to="/settings/outreach/sequencing"
                  className="font-medium text-brand hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  Global Settings
                </Link>
                . Changes there apply to all campaigns.
              </span>
            </p>
          </div>
        </div>
      </div>

      <WizardStepFooter
        subtext={
          !hasContacts
            ? 'Add contacts from the campaign page to unlock Contacts and Launch steps.'
            : undefined
        }
      >
        <FbButton
          type="button"
          variant="outline"
          onClick={onBack}
          className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
        >
          <ChevronLeft className="size-4" />
          Back
        </FbButton>
        {hasContacts && !isLiveCampaign && !readOnly ? (
          <FbButton
            type="button"
            onClick={handleContinue}
            loading={syncCampaignLeadsMutation.isPending}
            disabled={syncCampaignLeadsMutation.isPending}
            className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
          >
            Continue to Launch
            <ChevronRight className="size-4" />
          </FbButton>
        ) : (
          <FbButton
            type="button"
            onClick={handleSaveExit}
            className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
          >
            <Save className="size-4" />
            Save & Exit
          </FbButton>
        )}
      </WizardStepFooter>
    </div>
  )
}

function WizardSenderDomainSection({
  campaignId,
  campaignStatus,
}: {
  campaignId: number
  campaignStatus?: string
}) {
  const isSendingActive = campaignStatus === WORKFLOW_STATUS.ACTIVE
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const { data: sequence } = useGetCampaignSequence(campaignId)
  const bisonCampaignId = sequence?.bison_campaign_id ?? 0
  const {
    data: senderEmails,
    isLoading,
    isFetching,
  } = useGetCampaignSenderEmails(bisonCampaignId)
  const syncMutation = useSyncWorkspaceSenderEmails()
  const detachMutation = useDetachCampaignSenderEmail(bisonCampaignId)

  const attachedSenderEmails = senderEmails?.results ?? []

  const handleSync = async () => {
    const [err, result] = await to(syncMutation.mutateAsync())
    if (err) {
      toast.error('Failed to sync sender emails')
      return
    }
    toast.success(`Synced: ${result.created} added, ${result.updated} updated`)
  }

  const handleRemove = async (senderEmailId: number) => {
    const [err] = await to(detachMutation.mutateAsync(senderEmailId))
    if (err) {
      toast.error('Failed to remove sender email')
      return
    }
    toast.success('Sender email removed from campaign')
  }

  if (!bisonCampaignId) {
    return (
      <div className="rounded-xl border border-dashed border-line bg-surface-alt px-5 py-6 text-sm text-fg-secondary">
        Create and save a sequence first, then come back to attach sender
        emails.
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-fg-secondary">
            {attachedSenderEmails.length > 0
              ? `${attachedSenderEmails.length} sender email${attachedSenderEmails.length === 1 ? '' : 's'} attached`
              : 'No sender emails attached yet'}
          </div>
          <div className="flex flex-wrap gap-2">
            <FbButton
              variant="secondary"
              size="sm"
              onClick={handleSync}
              disabled={syncMutation.isPending}
              loading={syncMutation.isPending}
              className="gap-1.5"
            >
              <RefreshCw className="size-3.5" />
              Sync Workspace
            </FbButton>
            <FbButton
              variant="primary"
              size="sm"
              onClick={() => setIsAddModalOpen(true)}
              className="gap-1.5"
            >
              <Plus className="size-3.5" />
              Add Sender Emails
            </FbButton>
          </div>
        </div>

        {isLoading || isFetching ? (
          <div className="rounded-xl border border-line bg-surface-alt px-5 py-8 text-center text-sm text-fg-secondary">
            Loading sender emails...
          </div>
        ) : attachedSenderEmails.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line bg-surface-alt px-5 py-8">
            <div className="mx-auto max-w-lg text-center">
              <div className="mx-auto mb-3 flex size-11 items-center justify-center rounded-xl bg-brand/10">
                <Mail className="size-5 text-brand" />
              </div>
              <h3 className="text-sm font-semibold text-fg">
                Choose one or more sender emails
              </h3>
              <p className="mt-1 text-sm text-fg-secondary">
                Attached sender accounts determine which inboxes and domains can
                send this campaign.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl border border-brand/20 bg-brand/5 px-4 py-3">
              <p className="flex items-center gap-2 text-xs text-fg">
                <CheckCircle2 className="size-4 text-brand" />
                Emails will send from{' '}
                <span className="font-medium text-brand">
                  {attachedSenderEmails.length === 1
                    ? attachedSenderEmails[0].email
                    : `${attachedSenderEmails.length} connected sender emails`}
                </span>
                .
              </p>
            </div>

            <div className="space-y-2">
              {attachedSenderEmails.map((senderEmail) => (
                <div
                  key={senderEmail.id}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-white px-4 py-3"
                >
                  <div className="flex size-9 items-center justify-center rounded-full bg-brand/10 text-xs font-semibold text-brand">
                    {getSenderInitials(senderEmail)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-medium text-fg">
                        {senderEmail.name || senderEmail.email}
                      </p>
                      <span
                        className={cn(
                          'inline-flex rounded-full px-2 py-0.5 text-xs',
                          getSenderStatusClassName(senderEmail.status)
                        )}
                      >
                        {getSenderStatusLabel(senderEmail.status)}
                      </span>
                    </div>
                    <p className="truncate text-xs text-fg-secondary">
                      {senderEmail.email}
                    </p>
                  </div>
                  <div className="text-right text-xs text-fg-secondary">
                    <p>Daily limit</p>
                    <p className="font-medium text-fg">
                      {senderEmail.daily_limit}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemove(senderEmail.id)}
                    disabled={detachMutation.isPending || isSendingActive}
                    className="flex size-8 items-center justify-center rounded-lg text-fg-secondary transition-colors hover:bg-error-bg hover:text-error disabled:cursor-not-allowed disabled:opacity-50"
                    title={
                      isSendingActive
                        ? 'Cannot remove sender emails while campaign is actively sending. Pause the campaign first.'
                        : 'Remove sender email'
                    }
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <AddSenderEmailsModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        bisonCampaignId={bisonCampaignId}
      />
    </>
  )
}

const FORMAT_OPTIONS: Array<{
  value: boolean
  title: string
  description: string
}> = [
  {
    value: true,
    title: 'Plain text (recommended)',
    description: 'Best deliverability for cold outreach.',
  },
  {
    value: false,
    title: 'HTML',
    description: 'For warm comms, rebate links, GIFs, transactional.',
  },
]

function WizardEmailFormatSection({ campaignId }: { campaignId: number }) {
  const { data: sequence } = useGetCampaignSequence(campaignId)
  const hasBisonCampaign = (sequence?.bison_campaign_id ?? 0) > 0
  // The settings endpoint is keyed by the Lonesome Campaign.id, not the
  // Bison campaign id. We still gate on hasBisonCampaign so the call only
  // fires once a sequence (and its BisonCampaign) has been created.
  const { data: settings, isLoading } = useGetCampaignSettings(
    campaignId,
    hasBisonCampaign
  )
  const updateMutation = useUpdateCampaignSettings(campaignId)

  const plainText = settings?.plain_text ?? true

  const handleChange = async (nextPlainText: boolean) => {
    if (nextPlainText === plainText) return
    const [err] = await to(
      updateMutation.mutateAsync({ plain_text: nextPlainText })
    )
    if (err) {
      const data = axios.isAxiosError(err)
        ? (err.response?.data as Record<string, unknown> | undefined)
        : undefined
      const plainTextField = data?.plain_text
      const detail =
        (typeof data?.detail === 'string' && data.detail) ||
        (typeof plainTextField === 'string' && plainTextField) ||
        (Array.isArray(plainTextField) &&
          typeof plainTextField[0] === 'string' &&
          plainTextField[0]) ||
        null
      toast.error(detail || 'Failed to update email format')
    }
  }

  if (!hasBisonCampaign) {
    return (
      <div className="rounded-xl border border-dashed border-line bg-surface-alt px-5 py-6 text-sm text-fg-secondary">
        Create and save a sequence first, then come back to choose a format.
      </div>
    )
  }
  if (isLoading) {
    return <div className="py-4 text-sm text-fg-secondary">Loading format…</div>
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2">
        {FORMAT_OPTIONS.map((option) => {
          const isSelected = option.value === plainText
          return (
            <label
              key={option.title}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition-all',
                isSelected
                  ? 'border-brand bg-brand/5 ring-2 ring-brand/15'
                  : 'border-line hover:border-brand/30'
              )}
            >
              <input
                type="radio"
                name="email-format"
                checked={isSelected}
                onChange={() => handleChange(option.value)}
                className="mt-1 accent-brand"
              />
              <div className="flex-1">
                <p
                  className={cn(
                    'text-sm font-medium',
                    isSelected ? 'text-brand' : 'text-fg'
                  )}
                >
                  {option.title}
                </p>
                <p className="mt-0.5 text-xs text-fg-secondary">
                  {option.description}
                </p>
              </div>
            </label>
          )
        })}
      </div>

      {!plainText && (
        <div className="flex items-start gap-2 rounded-xl border border-warning-border bg-warning-bg px-4 py-3 text-xs text-warning">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>
            HTML can hurt deliverability for cold outreach. We recommend plain
            text unless you need formatting (rebate links, GIFs, transactional).
          </span>
        </div>
      )}
    </div>
  )
}

const WizardScheduleSection = forwardRef<
  WizardSectionHandle,
  { campaignId: number }
>(function WizardScheduleSection({ campaignId }, ref) {
  const { data: schedule, isLoading } = useGetCampaignSchedule(campaignId)
  const updateScheduleMutation = useUpdateCampaignSchedule(campaignId)

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
    setError,
    setValue,
    watch,
  } = useForm<CampaignScheduleWizardForm>({
    defaultValues: SCHEDULE_DEFAULT_VALUES,
    mode: 'onChange',
  })

  const values = watch()

  useEffect(() => {
    if (!schedule) {
      reset(SCHEDULE_DEFAULT_VALUES)
      return
    }

    reset({
      monday: schedule.monday,
      tuesday: schedule.tuesday,
      wednesday: schedule.wednesday,
      thursday: schedule.thursday,
      friday: schedule.friday,
      saturday: schedule.saturday,
      sunday: schedule.sunday,
      start_time: toTimePickerFormat(schedule.start_time),
      end_time: toTimePickerFormat(schedule.end_time),
      timezone: schedule.timezone,
    })
  }, [reset, schedule])

  const activePreset = useMemo(() => {
    return SCHEDULE_PRESETS.find((preset) => {
      return (
        DAYS.every((day) => preset.values[day.key] === values[day.key]) &&
        preset.values.start_time === values.start_time &&
        preset.values.end_time === values.end_time
      )
    })?.label
  }, [values])

  const persistValues = useCallback(
    async (nextValues: CampaignScheduleWizardForm): Promise<boolean> => {
      const [err] = await to(updateScheduleMutation.mutateAsync(nextValues))
      if (err) {
        handleFormError(err, setError)
        toast.error('Failed to save schedule')
        return false
      }
      reset(nextValues)
      return true
    },
    [reset, setError, updateScheduleMutation]
  )

  const submitIfDirty = useCallback(async (): Promise<boolean> => {
    // Force save if no schedule has been created yet on Bison,
    // even if the form isn't "dirty" (default values were never persisted)
    if (!isDirty && schedule) return true

    let success = false
    await handleSubmit(async (nextValues) => {
      success = await persistValues(nextValues)
    })()
    return success
  }, [handleSubmit, isDirty, persistValues, schedule])

  useImperativeHandle(ref, () => ({ submitIfDirty }), [submitIfDirty])

  if (isLoading) {
    return (
      <div className="py-8 text-sm text-fg-secondary">Loading schedule...</div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="mb-2 block text-xs uppercase tracking-wider text-fg-secondary">
          Quick Presets
        </label>
        <div className="grid gap-2 md:grid-cols-2">
          {SCHEDULE_PRESETS.map((preset) => {
            const isActive = activePreset === preset.label
            return (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  DAYS.forEach((day) => {
                    setValue(day.key, preset.values[day.key], {
                      shouldDirty: true,
                    })
                  })
                  setValue('start_time', preset.values.start_time, {
                    shouldDirty: true,
                  })
                  setValue('end_time', preset.values.end_time, {
                    shouldDirty: true,
                  })
                }}
                className={cn(
                  'rounded-xl border px-4 py-3 text-left transition-all',
                  isActive
                    ? 'border-brand bg-brand/5 shadow-sm ring-2 ring-brand/15'
                    : 'border-line hover:border-brand/30'
                )}
              >
                <p
                  className={cn(
                    'text-sm font-medium',
                    isActive ? 'text-brand' : 'text-fg'
                  )}
                >
                  {preset.label}
                </p>
                <p
                  className={cn(
                    'mt-1 text-xs',
                    isActive ? 'text-brand/80' : 'text-fg-secondary'
                  )}
                >
                  {preset.description}
                </p>
              </button>
            )
          })}
        </div>
      </div>

      <div>
        <label className="mb-2 block text-xs uppercase tracking-wider text-fg-secondary">
          Send On These Days
        </label>
        <div className="flex flex-wrap gap-2">
          {DAYS.map((day) => {
            const isSelected = values[day.key]
            return (
              <button
                key={day.key}
                type="button"
                onClick={() =>
                  setValue(day.key, !isSelected, { shouldDirty: true })
                }
                className={cn(
                  'flex h-10 w-12 items-center justify-center rounded-lg text-xs transition-colors',
                  isSelected
                    ? 'bg-brand text-white'
                    : 'bg-surface-alt text-fg-secondary hover:bg-surface-hover'
                )}
              >
                {day.label}
              </button>
            )
          })}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr,1fr,240px]">
        <div>
          <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-secondary">
            Start Time
          </label>
          <Controller
            name="start_time"
            control={control}
            render={({ field }) => (
              <TimePicker
                value={field.value}
                onChange={(value) => field.onChange(value)}
                className="w-full"
              />
            )}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-secondary">
            End Time
          </label>
          <Controller
            name="end_time"
            control={control}
            render={({ field }) => (
              <TimePicker
                value={field.value}
                onChange={(value) => field.onChange(value)}
                className="w-full"
              />
            )}
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-secondary">
            Timezone
          </label>
          <Controller
            name="timezone"
            control={control}
            render={({ field }) => (
              <ReactSelectWrapper
                inputId="campaign-schedule-timezone"
                instanceId="campaign-schedule-timezone"
                menuPosition="fixed"
                isClearable={false}
                options={TIMEZONE_OPTIONS}
                value={
                  TIMEZONE_OPTIONS.find(
                    (option) => option.value === field.value
                  ) ?? null
                }
                onChange={(newValue: any) =>
                  field.onChange(newValue?.value ?? '')
                }
                placeholder="Select timezone"
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: 'unset',
                    height: 'calc(1.5em + 0.75rem + 2px)',
                    boxShadow: 'none',
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: 'calc(1.5em + 0.75rem)',
                    padding: '0 8px',
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: '0',
                    padding: '0',
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: 'calc(1.5em + 0.75rem)',
                  }),
                }}
              />
            )}
          />
        </div>
      </div>

      <div className="rounded-lg bg-surface-alt px-4 py-3 text-xs text-fg-secondary">
        <p className="flex items-start gap-2 leading-relaxed">
          <Globe className="mt-0.5 size-4 shrink-0 text-brand" />
          Emails are scheduled within this timezone window. EmailBison will
          still respect sender-level limits on top of the campaign window.
        </p>
      </div>
    </div>
  )
})

interface SettingsSectionProps {
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: string
  children: React.ReactNode
}

function SettingsSection({
  icon: Icon,
  title,
  description,
  children,
}: SettingsSectionProps) {
  return (
    <div className="rounded-xl border border-line bg-surface p-5">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex size-9 items-center justify-center rounded-lg bg-brand/10">
          <Icon className="size-4 text-brand" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-fg">{title}</h3>
          <p className="text-xs text-fg-secondary">{description}</p>
        </div>
      </div>
      {children}
    </div>
  )
}
